import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn';

/** Framed area on the page. Border instead of shadow, so pages stay calm. */
export function Card({ className, ...props }: ComponentProps<'section'>) {
  return (
    <section
      className={cn('overflow-hidden rounded-lg border border-line bg-surface', className)}
      {...props}
    />
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

/** On narrow screens the actions move below the title and wrap instead of squeezing it. */
export function CardHeader({ title, description, actions, className }: CardHeaderProps) {
  return (
    <header className={cn('flex flex-wrap items-start gap-3 px-4 pt-4 md:px-5', className)}>
      <div className="min-w-0 flex-[1_1_12rem]">
        <h2 className="text-base font-semibold text-fg">{title}</h2>
        {description && <p className="mt-0.5 text-fg-muted">{description}</p>}
      </div>
      {actions && <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function CardContent({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('p-4 md:px-5', className)} {...props} />;
}

export function CardFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 border-t border-line bg-canvas px-4 py-3 md:px-5',
        className,
      )}
      {...props}
    />
  );
}
